import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface LocationSelectorProps {
  profileId: string;
  initialCountryId?: string;
  initialRegionId?: string;
}

const LocationSelector = ({ profileId, initialCountryId, initialRegionId }: LocationSelectorProps) => {
  const [countries, setCountries] = useState<any[]>([]);
  const [regions, setRegions] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>(initialCountryId);
  const [selectedRegion, setSelectedRegion] = useState<string | undefined>(initialRegionId);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const { data, error } = await supabase
          .from('countries')
          .select('*')
          .order('name');

        if (error) throw error;
        setCountries(data);
      } catch (error: any) {
        toast.error(t('profile.location.loadCountriesError', { error: error.message }));
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  // Fetch regions when country is selected
  useEffect(() => {
    const fetchRegions = async () => {
      if (!selectedCountry) {
        setRegions([]);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('regions')
          .select('*')
          .eq('country_id', selectedCountry)
          .order('name');

        if (error) throw error;
        setRegions(data);
      } catch (error: any) {
        toast.error(t('profile.location.loadRegionsError', { error: error.message }));
      }
    };

    fetchRegions();
  }, [selectedCountry]);

  // Update profile when location is changed
  const updateProfileLocation = async (countryId?: string, regionId?: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          country_id: countryId,
          region_id: regionId
        })
        .eq('id', profileId);

      if (error) throw error;
      toast.success(t('profile.location.updateSuccess'));
    } catch (error: any) {
      toast.error(t('profile.location.updateError', { error: error.message }));
    }
  };

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
    setSelectedRegion(undefined);
    updateProfileLocation(value, undefined);
  };

  const handleRegionChange = (value: string) => {
    setSelectedRegion(value);
    updateProfileLocation(selectedCountry, value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 text-sangre-500 animate-spin" />
        <span className="ml-2">{t('profile.location.loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('profile.location.country')}
        </label>
        <Select value={selectedCountry} onValueChange={handleCountryChange}>
          <SelectTrigger>
            <SelectValue placeholder={t('profile.location.selectCountry')} />
          </SelectTrigger>
          <SelectContent>
            {countries.map((country) => (
              <SelectItem key={country.id} value={country.id}>
                {country.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedCountry && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('profile.location.region')}
          </label>
          <Select value={selectedRegion} onValueChange={handleRegionChange}>
            <SelectTrigger>
              <SelectValue placeholder={t('profile.location.selectRegion')} />
            </SelectTrigger>
            <SelectContent>
              {regions.map((region) => (
                <SelectItem key={region.id} value={region.id}>
                  {region.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
